<template>
    <div class="modal-backdrop">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="$emit('close')" aria-label="Close"><i class="fa fa-times"></i></button>
            <h5 class="modal-title">{{ isEdit ? 'Edit Supergroup' : 'Create Supergroup' }}</h5>
          </div>
          <form @submit.prevent="handleSubmit">
            <div class="modal-body">
              <div class="mb-3">
                <label for="name" class="form-label">Name</label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  required
                />
              </div>
              
              <div class="mb-3">
                <label for="parent_id" class="form-label">Parent Supergroup</label>
                <select
                  id="parent_id"
                  v-model="form.parent_id"
                  class="form-select"
                >
                  <option value="">None (Root Level)</option>
                  <option 
                    v-for="sg in supergroupsWithHierarchy" 
                    :key="sg.uuid" 
                    :value="sg.uuid"
                    :disabled="selectedSupergroup && sg.uuid === selectedSupergroup.uuid"
                  >
                    {{ sg.displayName }}
                  </option>
                </select>
              </div>
              
              <div class="mb-3">
                <label for="tags" class="form-label">Tags (comma separated)</label>
                <input
                  id="tags"
                  v-model="tagsInput"
                  type="text"
                  class="form-control"
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>
            
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Saving...' : 'Save' }}
              </button>
              <button
                type="button"
                @click="$emit('close')"
                class="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { getSupergroupHierarchy } from '@/admin_entities/supergroups/supergroup_utils'
  export default {
    name: 'SupergroupForm',
    props: {
      selectedSupergroup: {
        type: Object,
        default: null
      },
      allSupergroups: {
        type: Array,
        required: true
      },
      isEdit: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        form: {
          name: '',
          parent_id: '',
          tags: []
        },
        tagsInput: '',
        isSubmitting: false
      };
    },
    computed: {
      supergroupMap() {
        return new Map(this.allSupergroups.map(sg => [sg.uuid, sg]));
      },
      supergroupsWithHierarchy() {
        return this.availableParents.map(sg => ({
          ...sg,
          displayName: `${sg.name} ${this.getSupergroupHierarchy(sg) ? `(${this.getSupergroupHierarchy(sg)})` : ''}`
        }));
      },
      availableParents() {
        if (!this.selectedSupergroup) {
          return this.allSupergroups;
        }
        
        // Filter out the current supergroup and its descendants to prevent circular references
        const descendants = this.getDescendantIds(this.selectedSupergroup.uuid);
        return this.allSupergroups.filter(sg => 
          sg.uuid !== this.selectedSupergroup.uuid && !descendants.includes(sg.uuid)
        );
      }
    },
    created() {
      if (this.selectedSupergroup) {
        this.form = {
          name: this.selectedSupergroup.name,
          parent_id: this.selectedSupergroup.parent_id || '',
          tags: [...(this.selectedSupergroup.tags || [])]
        };
        this.tagsInput = (this.selectedSupergroup.tags || []).join(', ');
      }
    },
    methods: {
      getSupergroupHierarchy(supergroup) {
        return getSupergroupHierarchy(supergroup, this.supergroupMap);
      },
      getDescendantIds(parent_id) {
        const descendants = [];
        const children = this.allSupergroups.filter(sg => sg.parent_id === parent_id);
        
        children.forEach(child => {
          descendants.push(child.uuid);
          descendants.push(...this.getDescendantIds(child.uuid));
        });
        
        return descendants;
      },

      handleSubmit() {
        if (this.isSubmitting) return;
        
        this.isSubmitting = true;
        
        // Parse tags from comma-separated input
        const tags = this.tagsInput
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag);
        
        const supergroupData = {
          ...this.form,
          tags
        };
        
        // If editing, include the UUID
        if (this.isEdit && this.selectedSupergroup) {
          supergroupData.uuid = this.selectedSupergroup.uuid;
        }
        
        // Convert empty parent_id to null
        if (!supergroupData.parent_id) {
          supergroupData.parent_id = null;
        }
        
        this.$emit('save', supergroupData);
        this.isSubmitting = false;
      }
    }
  };
  </script>
  <style scoped>
  .mb-3 {
    margin-bottom: 12px;
  }
  </style>